// The relying-party OIDC client (spec 295 / spec 230 / ADR-0019). A relying app does NOT run a broker and
// originates NO credential: it builds an `/authorize` request to the user's central auth Home, and the Home
// runs the credential ceremony (wallet / Google / YouVersion / passkey) + custody, then redirects back with
// `?code=…&state=…`. This module owns ONLY that relying-side protocol — PKCE, the authorize URL, the /token
// exchange, and ES256 id_token verification.
//
// GENERIC (ADR-0021): no hostnames, no `.impact` TLD, no host/subdomain parsing. The app injects a config
// object (`resolveAuthOrigin` / `isAllowedIssuerOrigin` / `clientId` / `delegate` / `redirectUri`) wiring its
// own `domain.ts`. The credential ceremonies live at the Home (connect-auth), never here.
// ── base64url + JWT segment helpers (WebCrypto only) ───────────────────
function b64url(bytes) {
    let s = '';
    for (const b of bytes)
        s += String.fromCharCode(b);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromB64url(seg) {
    const bin = atob(seg.replace(/-/g, '+').replace(/_/g, '/'));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
}
function decodeJwtSegment(seg) {
    return JSON.parse(new TextDecoder().decode(fromB64url(seg)));
}
/** A random base64url string of `n` bytes (state / nonce / PKCE verifier). */
export function randomB64url(n) {
    return b64url(crypto.getRandomValues(new Uint8Array(n)));
}
/** PKCE S256: a random verifier + `base64url(SHA-256(verifier))` challenge (RFC 7636). */
export async function generatePkce() {
    const verifier = randomB64url(32);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return { verifier, challenge: b64url(new Uint8Array(digest)) };
}
/** Construct the relying-party OIDC client for one app. All app-specific identity (client_id, delegate,
 *  redirect, the Home-origin resolver, the issuer allowlist) is injected — keeping this package generic. */
export function createConnectClient(config) {
    const scope = config.scope ?? 'openid agent';
    const buildAuthorizeUrl = (p) => {
        const u = new URL('/', p.authOrigin);
        u.searchParams.set('client_id', config.clientId);
        u.searchParams.set('redirect_uri', config.redirectUri());
        u.searchParams.set('response_type', 'code');
        u.searchParams.set('scope', scope);
        u.searchParams.set('state', p.state);
        u.searchParams.set('nonce', p.nonce);
        u.searchParams.set('code_challenge', p.codeChallenge);
        u.searchParams.set('code_challenge_method', 'S256');
        u.searchParams.set('agent_name', p.agentName);
        u.searchParams.set('delegate', config.delegate);
        u.searchParams.set('delegation_template', p.template);
        if (p.orgBase)
            u.searchParams.set('org_base', p.orgBase);
        if (p.purpose)
            u.searchParams.set('org_purpose', p.purpose);
        if (p.grantOrg)
            u.searchParams.set('grant_org', p.grantOrg);
        if (p.existingOrg)
            u.searchParams.set('existing_org', p.existingOrg);
        if (p.mode)
            u.searchParams.set('mode', p.mode);
        return u.toString();
    };
    const begin = async (name, template, extra = {}) => {
        const state = randomB64url(16);
        const nonce = randomB64url(16);
        const { verifier, challenge } = await generatePkce();
        const authOrigin = await config.resolveAuthOrigin(name);
        const url = buildAuthorizeUrl({ authOrigin, state, nonce, codeChallenge: challenge, agentName: name, template, ...extra });
        return { ok: true, url, state, authOrigin, codeVerifier: verifier, nonce };
    };
    const decodeIdToken = (idToken) => decodeJwtSegment(idToken.split('.')[1] ?? '');
    return {
        buildAuthorizeUrl,
        startEnrollment: (name, opts) => begin(name, 'site-login', { mode: opts?.mode }),
        startOrgCreation: (personName, orgBase, opts) => begin(personName, 'org-create', { orgBase, purpose: opts?.purpose, grantOrg: opts?.grantOrg, existingOrg: opts?.existingOrg, mode: opts?.mode }),
        startWorkspaceCreation: (personName, workspaceName, opts) => begin(personName, 'workspace-create', { orgBase: workspaceName, purpose: opts?.purpose, mode: opts?.mode }),
        async exchangeCode(authOrigin, code, codeVerifier) {
            const r = await fetch(new URL('/token', authOrigin).toString(), {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ grant_type: 'authorization_code', code, code_verifier: codeVerifier, client_id: config.clientId, redirect_uri: config.redirectUri() }),
            });
            const b = (await r.json().catch(() => ({})));
            if (!r.ok || !b.id_token)
                throw new Error(b.error ?? `token exchange failed (HTTP ${r.status})`);
            return { idToken: b.id_token, delegation: b.delegation, org: b.org, selfVaultGrant: b.selfVaultGrant };
        },
        async verifyIdToken(authOrigin, idToken, expectedNonce) {
            if (!config.isAllowedIssuerOrigin(authOrigin)) {
                throw new Error(`refusing to verify id_token: issuer "${authOrigin}" not in allowlist`);
            }
            const parts = idToken.split('.');
            if (parts.length !== 3)
                throw new Error('id_token malformed');
            const [h, p, s] = parts;
            const header = decodeJwtSegment(h);
            const claims = decodeJwtSegment(p);
            const { keys } = (await (await fetch(new URL('/jwks', authOrigin).toString())).json());
            const jwk = keys.find((k) => k.kid === header.kid);
            if (!jwk)
                throw new Error('no JWKS key for id_token kid');
            if (jwk.alg !== 'ES256' || header.alg !== 'ES256')
                throw new Error('id_token alg not ES256');
            const key = await crypto.subtle.importKey('jwk', jwk, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
            const ok = await crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, fromB64url(s), new TextEncoder().encode(`${h}.${p}`));
            if (!ok)
                throw new Error('id_token signature invalid');
            if (claims.iss !== authOrigin)
                throw new Error('id_token iss mismatch');
            if (!config.isAllowedIssuerOrigin(claims.iss))
                throw new Error('id_token iss not in allowlist');
            if (claims.aud !== config.clientId)
                throw new Error('id_token aud mismatch');
            if (expectedNonce && claims.nonce !== expectedNonce)
                throw new Error('id_token nonce mismatch');
            if (typeof claims.exp !== 'number' || claims.exp * 1000 <= Date.now())
                throw new Error('id_token expired');
            return claims;
        },
        decodeIdToken,
        personAddressFromIdToken(idToken) {
            const c = decodeIdToken(idToken);
            const raw = c.canonical_agent_id ?? c.sub;
            return (raw.includes(':') ? raw.split(':').pop() : raw);
        },
        isAllowedIssuerOrigin: config.isAllowedIssuerOrigin,
    };
}
//# sourceMappingURL=oidc.js.map